import React, { useState } from 'react';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { Checkbox } from '../CommonComponents/OPL/Checkbox/Checkbox';
import { Modal } from '../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../CommonComponents/OPL/Modal/ModalHeader';
import { Tab, TabList, TabPanel, Tabs } from '../CommonComponents/OPL/Tab';
import { AccountDistributionOrderList } from './AccountDistributionOrderList';
import { AccountDistributionOrderItemId } from './SortableAccountDistributionItem';
import {
  AccountDistributionOrderOptionDTO,
  CurrentDistributionOrderDTO,
} from './api/useAccountDistributionOptionsQuery';

interface AccountDistributionOrderModalProps {
  isOpen: boolean;
  onClose(): void;
  options: AccountDistributionOrderOptionDTO[];
  defaultOrder: AccountDistributionOrderItemId[];
  order: CurrentDistributionOrderDTO;
  onOrderChange(order: CurrentDistributionOrderDTO): void;
}

export const AccountDistributionOrderModal = ({
  isOpen,
  onClose,
  options,
  defaultOrder,
  order,
  onOrderChange,
}: AccountDistributionOrderModalProps) => {
  const [preRetirementOptions, setPreRetirementOptions] = useState(
    order.preRetirement
      .map(id => options.find(opt => opt.id === id) as AccountDistributionOrderOptionDTO)
      .filter(Boolean),
  );

  const [postRetirementOptions, setPostRetirementOptions] = useState(
    order.postRetirement
      ?.map(id => options.find(opt => opt.id === id) as AccountDistributionOrderOptionDTO)
      .filter(Boolean) ?? null,
  );

  const defaultOptions = defaultOrder.map(
    id => options.find(opt => opt.id === id) as AccountDistributionOrderOptionDTO,
  );

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} aria={{ labelledby: 'modal-header' }}>
      <ModalHeader id="modal-header">Default Distribution Order</ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody>
        <Checkbox
          checked={!!postRetirementOptions}
          onChange={e =>
            setPostRetirementOptions(
              e.target.checked ? postRetirementOptions ?? defaultOptions : null,
            )
          }
          mb="8"
        >
          Use a different distribution order in retirement
        </Checkbox>

        <Tabs>
          {postRetirementOptions && (
            <TabList mb="16">
              <Tab>Pre-Retirement</Tab>
              <Tab>In Retirement</Tab>
            </TabList>
          )}

          <TabPanel>
            <AccountDistributionOrderList
              options={options}
              defaultOrder={defaultOptions}
              order={preRetirementOptions}
              onOrderChange={setPreRetirementOptions}
            />
          </TabPanel>

          {postRetirementOptions && (
            <TabPanel>
              <AccountDistributionOrderList
                options={options}
                defaultOrder={defaultOptions}
                order={postRetirementOptions}
                onOrderChange={setPostRetirementOptions}
              />
            </TabPanel>
          )}
        </Tabs>
      </ModalBody>

      <ModalFooter>
        <Button variant="subtleInactive" onClick={onClose}>
          Cancel
        </Button>

        <Button
          ml="8"
          onClick={() => {
            onOrderChange({
              preRetirement: preRetirementOptions.map(o => o.id),
              postRetirement: postRetirementOptions?.map(o => o.id) ?? null,
            });

            onClose();
          }}
        >
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};
